import { MonoLabel, Skeleton } from '@/design-system';
import { smsLength, type MessageDraftInput, type MessageTemplate } from '@/entities/notification';
import { errorMessage } from '@/shared/api';

import { clientMessagesCopy as copy } from '../copy';
import { useMessagePreview } from '../hooks';

const SMALL = 'text-[12px] leading-[1.45] text-[var(--text-tertiary-safe)]';

export interface MessagePreviewPanelProps {
    template: MessageTemplate;
    /** Wat er nu in de velden staat; `null` zolang de tekst een fout heeft. */
    draft: MessageDraftInput | null;
    companyName: string;
    /** Alleen voor tests: hoe lang het voorbeeld wacht na het typen. */
    delayMs?: number;
}

/**
 * Het voorbeeld naast de editor: de tekst met voorbeeldgegevens, zoals de klant
 * hem krijgt.
 *
 * Een e-mail komt van de server, uit dezelfde mailview als een echt bericht:
 * logo, accentkleur, knop en voet van het bedrijf. Hij staat in een afgeschermd
 * frame zonder scripts, zodat de opmaak van de mail en die van de app elkaar
 * niet raken. Een sms staat in een telefoon, met hoeveel tekens en berichten hij
 * met voorbeeldgegevens kost.
 *
 * Terwijl je typt blijft het vorige voorbeeld staan; het nieuwe komt zodra je
 * even stopt.
 */
export function MessagePreviewPanel({
    template,
    draft,
    companyName,
    delayMs,
}: MessagePreviewPanelProps) {
    const { preview, error, updating } = useMessagePreview(draft, delayMs);
    const sender = preview?.values.tenant_name ?? companyName;

    return (
        <section aria-labelledby={`preview-${template.channel}`} className="min-w-0">
            <div className="flex flex-wrap items-baseline justify-between gap-2">
                <h3
                    id={`preview-${template.channel}`}
                    className="m-0 font-[family-name:var(--font-display)] text-[15px] leading-[1.3] font-semibold text-[var(--text-primary)]"
                >
                    {copy.previewTitle}
                </h3>
                <span aria-live="polite" className="min-h-[13px]">
                    {updating && preview ? (
                        <MonoLabel size={10} tone="muted">
                            {copy.previewUpdating}
                        </MonoLabel>
                    ) : null}
                </span>
            </div>
            <p className={`m-0 mt-1 mb-3 ${SMALL}`}>{copy.previewIntro}</p>

            {draft === null ? <p className={`m-0 mb-3 ${SMALL}`}>{copy.previewWaiting}</p> : null}

            {error && draft !== null ? (
                <p
                    role="alert"
                    className="m-0 mb-3 text-[12px] leading-[1.45] text-[var(--danger)]"
                >
                    {errorMessage(error)}
                </p>
            ) : null}

            {preview === null ? (
                draft === null ? null : (
                    <Skeleton
                        height={template.channel === 'mail' ? 560 : 220}
                        radius={10}
                        label={copy.previewLoading}
                    />
                )
            ) : template.channel === 'mail' ? (
                <MailPreview
                    label={template.keyLabel}
                    sender={sender}
                    subject={preview.subject ?? ''}
                    html={preview.html ?? ''}
                />
            ) : (
                <SmsPreview sender={sender} body={preview.body} />
            )}
        </section>
    );
}

function MailPreview({
    label,
    sender,
    subject,
    html,
}: {
    label: string;
    sender: string;
    subject: string;
    html: string;
}) {
    return (
        <div className="overflow-hidden rounded-[10px] border border-[var(--border-default)] bg-[var(--surface-card)]">
            <dl className="m-0 grid grid-cols-[auto_minmax(0,1fr)] gap-x-3 gap-y-1 border-b border-[var(--border-default)] bg-[var(--surface-subtle)] px-4 py-3 text-[13px] leading-[1.4]">
                <dt className="text-[var(--text-tertiary-safe)]">{copy.previewFrom}</dt>
                <dd className="m-0 truncate text-[var(--text-primary)]">{sender}</dd>
                <dt className="text-[var(--text-tertiary-safe)]">{copy.previewSubject}</dt>
                <dd className="m-0 font-medium break-words text-[var(--text-primary)]">
                    {subject}
                </dd>
            </dl>
            <iframe
                title={copy.previewFrame(label)}
                srcDoc={html}
                sandbox=""
                className="block h-[560px] w-full border-0 bg-[var(--surface-board)]"
            />
        </div>
    );
}

function SmsPreview({ sender, body }: { sender: string; body: string }) {
    const length = smsLength(body);

    return (
        <div>
            <div className="mx-auto max-w-[320px] rounded-[22px] border border-[var(--placeholder-border)] bg-[var(--surface-question-head)] p-[10px]">
                <span
                    aria-hidden="true"
                    className="mx-auto mt-[2px] mb-[10px] block h-[5px] w-[42px] rounded-[3px] bg-[var(--placeholder-border)]"
                />
                <div className="rounded-[14px] border border-[var(--border-phone-mock)] bg-[var(--surface-card)] p-[13px]">
                    <div className="mb-[7px] text-[11px] leading-[1.3] text-[var(--text-muted-mono-safe)]">
                        {sender}
                    </div>
                    <p
                        data-testid="sms-preview-body"
                        className="m-0 text-[13px] leading-[1.5] break-words whitespace-pre-wrap text-[var(--text-primary)]"
                    >
                        {body}
                    </p>
                </div>
            </div>
            <p className={`m-0 mt-3 ${SMALL}`}>{copy.smsParts(length.characters, length.parts)}</p>
            {length.encoding === 'unicode' ? (
                <p className={`m-0 mt-1 ${SMALL}`}>{copy.smsUnicode}</p>
            ) : null}
        </div>
    );
}
